import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCustomerAuth } from '../../context/CustomerAuthContext';
import { useToast } from '../../context/ToastContext';
import { Eye, EyeOff, Sun, Moon } from 'lucide-react';

export default function RegisterPage() {
  const { register } = useCustomerAuth();
  const toast        = useToast();
  const navigate     = useNavigate();

  const [form, setForm]       = useState({ display_name: '', phone: '', email: '', password: '', confirm: '' });
  const [showPw,  setShowPw]  = useState(false);
  const [showCfm, setShowCfm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState('');
  const [theme,   setTheme]   = useState(() => localStorage.getItem('wt-theme') || 'dark');
  const isLight = theme === 'light';

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('wt-theme', theme);
  }, [theme]);

  const set = k => e => setForm(p => ({ ...p, [k]: e.target.value }));

  const submit = async e => {
    e.preventDefault();
    if (form.password !== form.confirm) { toast.error('كلمتا المرور غير متطابقتين'); return; }
    if (form.password.length < 6)       { toast.error('كلمة المرور قصيرة جداً (6 أحرف على الأقل)'); return; }
    setLoading(true);
    try {
      await register({ display_name: form.display_name, phone: form.phone, email: form.email || undefined, password: form.password });
      navigate('/customer/home', { replace: true });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const C = {
    pageBg:      isLight ? 'linear-gradient(145deg,#dbeafe 0%,#bae6fd 50%,#e0f2fe 100%)'
                         : 'linear-gradient(145deg,#010c18 0%,#03111e 55%,#051828 100%)',
    cardBg:      isLight ? 'rgba(255,255,255,0.75)' : 'rgba(4,18,36,0.75)',
    cardBorder:  isLight ? 'rgba(10,186,181,0.22)'  : 'rgba(10,186,181,0.18)',
    cardShadow:  isLight ? '0 24px 60px rgba(0,0,0,0.10)' : '0 24px 60px rgba(0,0,0,0.45)',
    inputBg:     isLight ? 'rgba(240,249,255,0.85)' : 'rgba(1,12,26,0.65)',
    textPrimary: isLight ? '#0c2340' : 'rgba(240,249,255,0.95)',
    textMuted:   isLight ? '#3d7ca0' : 'rgba(186,230,253,0.48)',
    toggleBg:    isLight ? 'rgba(0,0,0,0.07)'  : 'rgba(255,255,255,0.09)',
    toggleBorder:isLight ? 'rgba(0,0,0,0.1)'   : 'rgba(255,255,255,0.14)',
    toggleColor: isLight ? '#0c2340' : 'rgba(240,249,255,0.8)',
  };

  const inputBorder = n => focused === n
    ? '1.5px solid rgba(10,186,181,0.75)'
    : `1.5px solid ${isLight ? 'rgba(10,186,181,0.18)' : 'rgba(10,186,181,0.12)'}`;
  const inputShadow = n => focused === n ? '0 0 0 3px rgba(10,186,181,0.13)' : 'none';

  const inputSt = n => ({
    outline:'none', width:'100%', boxSizing:'border-box',
    padding:'0.72rem 0.85rem',
    borderRadius:'0.75rem', fontSize:'0.875rem', fontWeight:500,
    background: C.inputBg, border: inputBorder(n), color: C.textPrimary,
    boxShadow: inputShadow(n), transition:'border 0.2s,box-shadow 0.2s',
    fontFamily:"'Cairo',sans-serif",
  });

  const fields = [
    { label:'الاسم الكامل',                    key:'display_name', type:'text',  placeholder:'أدخل اسمك الكامل', required:true },
    { label:'رقم الهاتف',                      key:'phone',        type:'tel',   placeholder:'05xxxxxxxx',        required:true, dir:'ltr' },
    { label:'البريد الإلكتروني (اختياري)',     key:'email',        type:'email', placeholder:'example@email.com', dir:'ltr' },
  ];

  return (
    <div dir="rtl" style={{
      minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center',
      padding:'1rem', background: C.pageBg, fontFamily:"'Cairo',sans-serif",
      position:'relative', overflow:'hidden',
    }}>
      <style>{`
        @keyframes wt-blob { 0%,100%{transform:translate(0,0) scale(1)} 40%{transform:translate(28px,-18px) scale(1.06)} 70%{transform:translate(-16px,14px) scale(0.96)} }
        @keyframes wt-in   { from{opacity:0;transform:translateY(22px) scale(0.96)} to{opacity:1;transform:translateY(0) scale(1)} }
        @keyframes wt-logo { from{opacity:0;transform:translateY(-16px) scale(0.88)} to{opacity:1;transform:translateY(0) scale(1)} }
        @keyframes wt-spin { to{transform:rotate(360deg)} }
        .wt-reg-input::placeholder { opacity:0.45; }
      `}</style>

      {/* Blobs */}
      <div style={{ position:'absolute', width:400, height:400, borderRadius:'50%', top:'-12%', right:'-12%',
        background: isLight ? 'rgba(10,186,181,0.13)' : 'rgba(10,186,181,0.08)',
        filter:'blur(55px)', animation:'wt-blob 10s ease-in-out infinite', pointerEvents:'none' }}/>
      <div style={{ position:'absolute', width:280, height:280, borderRadius:'50%', bottom:'-8%', left:'-8%',
        background: isLight ? 'rgba(56,189,248,0.14)' : 'rgba(2,80,140,0.1)',
        filter:'blur(45px)', animation:'wt-blob 13s ease-in-out infinite reverse', pointerEvents:'none' }}/>

      {/* Theme Toggle */}
      <button onClick={() => setTheme(t => t==='dark'?'light':'dark')} style={{
        position:'absolute', top:'1rem', left:'1rem',
        width:36, height:36, borderRadius:'50%', cursor:'pointer',
        background: C.toggleBg, border:`1px solid ${C.toggleBorder}`,
        backdropFilter:'blur(12px)', display:'flex', alignItems:'center', justifyContent:'center',
        color: C.toggleColor,
      }}>
        {isLight ? <Moon size={16}/> : <Sun size={16}/>}
      </button>

      <div style={{ width:'100%', maxWidth:'22rem', animation:'wt-in 0.4s cubic-bezier(0.34,1.2,0.64,1)' }}>

        {/* Logo */}
        <div style={{ textAlign:'center', marginBottom:'1.75rem', animation:'wt-logo 0.5s cubic-bezier(0.34,1.4,0.64,1)' }}>
          <img
            src={isLight ? '/assets/images/logo/blue-logo.png' : '/assets/images/logo/white-logo.png'}
            alt="WTRLY"
            style={{ height:60, width:'auto', objectFit:'contain', display:'block', margin:'0 auto 0.9rem' }}
            onError={e => {
              e.currentTarget.style.display = 'none';
              e.currentTarget.insertAdjacentHTML('afterend',
                `<svg width="48" height="48" viewBox="0 0 24 24" fill="${isLight?'#0ABAB5':'rgba(240,249,255,0.9)'}" style="margin-bottom:0.9rem;display:block;margin:0 auto 0.9rem"><path d="M12 2c-5.33 4.55-8 8.48-8 11.8 0 4.98 3.8 8.2 8 8.2s8-3.22 8-8.2c0-3.32-2.67-7.25-8-11.8z"/></svg>`
              );
            }}
          />
          <div style={{ fontSize:'1.7rem', fontWeight:900, letterSpacing:'-0.01em', color: C.textPrimary, lineHeight:1.05 }}>WTRLY</div>
          <div style={{ fontSize:'0.78rem', fontWeight:600, color: C.textMuted, marginTop:'0.25rem' }}>بوابة العملاء</div>
        </div>

        {/* Card */}
        <div style={{
          borderRadius:'1.25rem', overflow:'hidden',
          background: C.cardBg,
          backdropFilter:'blur(32px) saturate(200%)', WebkitBackdropFilter:'blur(32px) saturate(200%)',
          border:`1px solid ${C.cardBorder}`,
          boxShadow:`${C.cardShadow}, 0 0 0 1px rgba(10,186,181,0.07)`,
        }}>
          <div style={{ height:3, background:'linear-gradient(90deg,transparent,#0ABAB5 40%,rgba(56,189,248,0.8),transparent)' }}/>

          <div style={{ padding:'1.5rem' }}>

            {/* Back button + badge */}
            <div style={{ display:'flex', alignItems:'center', gap:'0.6rem', marginBottom:'1.2rem' }}>
              <a href="/login" style={{
                width:34, height:34, borderRadius:'50%',
                background: isLight ? 'rgba(10,186,181,0.1)' : 'rgba(255,255,255,0.09)',
                border:`1px solid ${isLight ? 'rgba(10,186,181,0.25)' : 'rgba(255,255,255,0.14)'}`,
                display:'flex', alignItems:'center', justifyContent:'center',
                color: C.textMuted, textDecoration:'none', flexShrink:0,
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M9 18l6-6-6-6"/></svg>
              </a>
              <div style={{
                padding:'0.3rem 0.9rem', borderRadius:'2rem',
                background:'linear-gradient(135deg,#0ABAB5,#0891b2)',
                color:'#fff', fontSize:'0.82rem', fontWeight:800,
              }}>عميل</div>
            </div>

            <h2 style={{ margin:'0 0 1.2rem', fontSize:'1.05rem', fontWeight:800, color: C.textPrimary }}>
              إنشاء حساب جديد
            </h2>

            <form onSubmit={submit} style={{ display:'flex', flexDirection:'column', gap:'0.75rem' }}>

              {fields.map(f => (
                <div key={f.key}>
                  <label style={{ display:'block', fontSize:'0.72rem', fontWeight:700, color:C.textMuted, marginBottom:'0.38rem' }}>
                    {f.label}
                  </label>
                  <input
                    className="wt-reg-input"
                    type={f.type} value={form[f.key]} onChange={set(f.key)}
                    placeholder={f.placeholder} required={f.required} dir={f.dir}
                    onFocus={() => setFocused(f.key)} onBlur={() => setFocused('')}
                    style={inputSt(f.key)}
                  />
                </div>
              ))}

              {/* Password */}
              {[
                { label:'كلمة المرور', key:'password', show:showPw, toggle:()=>setShowPw(p=>!p) },
                { label:'تأكيد كلمة المرور', key:'confirm', show:showCfm, toggle:()=>setShowCfm(p=>!p) },
              ].map(f => (
                <div key={f.key}>
                  <label style={{ display:'block', fontSize:'0.72rem', fontWeight:700, color:C.textMuted, marginBottom:'0.38rem' }}>
                    {f.label}
                  </label>
                  <div style={{ position:'relative' }}>
                    <input
                      className="wt-reg-input"
                      type={f.show ? 'text' : 'password'}
                      value={form[f.key]} onChange={set(f.key)}
                      placeholder="••••••••" required
                      onFocus={() => setFocused(f.key)} onBlur={() => setFocused('')}
                      style={{ ...inputSt(f.key), paddingLeft:'2.4rem' }}
                    />
                    <button type="button" onClick={f.toggle} style={{
                      position:'absolute', left:'0.7rem', top:'50%', transform:'translateY(-50%)',
                      background:'none', border:'none', cursor:'pointer', padding:'0.2rem',
                      color: f.show ? '#0ABAB5' : C.textMuted, display:'flex',
                    }}>
                      {f.show ? <EyeOff size={15}/> : <Eye size={15}/>}
                    </button>
                  </div>
                </div>
              ))}

              {/* Submit */}
              <button
                type="submit" disabled={loading}
                style={{
                  marginTop:'0.25rem', width:'100%', padding:'0.85rem',
                  borderRadius:'0.875rem', border:'none', cursor: loading ? 'not-allowed' : 'pointer',
                  background:'linear-gradient(135deg,#0ABAB5,#0891b2)',
                  color:'#fff', fontSize:'0.9rem', fontWeight:800,
                  boxShadow:'0 6px 24px rgba(10,186,181,0.38)',
                  opacity: loading ? 0.7 : 1, transition:'all 0.2s ease',
                  fontFamily:"'Cairo',sans-serif",
                  display:'flex', alignItems:'center', justifyContent:'center', gap:'0.5rem',
                }}
                onMouseEnter={e => { if (!loading) e.currentTarget.style.filter='brightness(1.08)'; }}
                onMouseLeave={e => { e.currentTarget.style.filter=''; }}
              >
                {loading ? (
                  <>
                    <span style={{
                      width:16, height:16, borderRadius:'50%',
                      border:'2px solid rgba(255,255,255,0.3)', borderTopColor:'#fff',
                      animation:'wt-spin 0.7s linear infinite', display:'inline-block',
                    }}/>
                    جاري الإنشاء...
                  </>
                ) : 'إنشاء الحساب'}
              </button>
            </form>

            {/* Divider */}
            <div style={{ display:'flex', alignItems:'center', gap:'0.5rem', margin:'1.1rem 0 0.9rem' }}>
              <div style={{ flex:1, height:1, background: isLight ? 'rgba(10,186,181,0.12)' : 'rgba(56,189,248,0.1)' }}/>
              <span style={{ fontSize:'0.58rem', fontWeight:800, letterSpacing:'0.16em', color:C.textMuted, userSelect:'none' }}>WTRLY</span>
              <div style={{ flex:1, height:1, background: isLight ? 'rgba(10,186,181,0.12)' : 'rgba(56,189,248,0.1)' }}/>
            </div>

            <div style={{ textAlign:'center' }}>
              <span style={{ fontSize:'0.78rem', color:C.textMuted }}>لديك حساب؟ </span>
              <Link to="/customer/login" style={{ fontSize:'0.78rem', fontWeight:800, color:'#0ABAB5', textDecoration:'none' }}>
                سجّل دخولك
              </Link>
            </div>
          </div>
        </div>

        <p style={{ textAlign:'center', color:C.textMuted, fontSize:'0.68rem', marginTop:'1.25rem', opacity:0.7 }}>
          © {new Date().getFullYear()} WTRLY — جميع الحقوق محفوظة
        </p>
      </div>
    </div>
  );
}
